import { useCallback, useEffect, useState } from 'react';
import { commentService } from '../services/commentService';
import { useEventSubscribe } from '../events/hooks';
import { isPermissionDenied } from '../utils/grpcErrors';
import { useAccessSettlingRetry } from './useAccessSettlingRetry';
// Per-project cache so the autocomplete vocabulary is hot across remounts.
const cache = new Map();
export const useProjectCommentTags = (projectId) => {
    const [tags, setTags] = useState(() => { var _a; return projectId ? (_a = cache.get(projectId)) !== null && _a !== void 0 ? _a : [] : []; });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const fetchTags = useCallback(async () => {
        if (!projectId) {
            setTags([]);
            return;
        }
        try {
            setIsLoading(true);
            setError(null);
            const list = await commentService.listProjectTags(projectId);
            cache.set(projectId, list);
            setTags(list);
            setDenied(false);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load tags');
            setDenied(isPermissionDenied(err));
        }
        finally {
            setIsLoading(false);
        }
    }, [projectId]);
    useAccessSettlingRetry(projectId, denied, fetchTags);
    useEffect(() => {
        if (!projectId) {
            setTags([]);
            return;
        }
        const cached = cache.get(projectId);
        if (cached) {
            setTags(cached);
        }
        fetchTags();
    }, [projectId, fetchTags]);
    // Refresh after any comment mutation — new comments may introduce new tags.
    useEventSubscribe('comments:updated', () => {
        fetchTags();
    }, [fetchTags]);
    return { tags, isLoading, error, refetch: fetchTags };
};
